import { ENTGELT_GROUPS } from '@hirebotiq-ops/core';
import { TRPCClientError } from '@trpc/client';
import { useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc.js';
import {
  buildSurchargeLadderInput,
  egLabel,
  emptyLadderStepDraft,
  type LadderStepDraft,
  type SurchargeLadderDraft,
} from '../../lib/surcharge-ladder-form.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.js';
import { Field } from '../ui/field.js';
import { Input } from '../ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select.js';
import { Textarea } from '../ui/textarea.js';

const GLOBAL_SCOPE = '__global__';

function localToday(): string {
  return new Intl.DateTimeFormat('sv-SE').format(new Date());
}

function initialDraft(): SurchargeLadderDraft {
  return {
    name: '',
    base: 'bill_rate',
    entgeltGroups: [],
    validFrom: localToday(),
    notes: '',
    steps: [emptyLadderStepDraft()],
  };
}

function validationMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'issues' in error) {
    const issues = (error as { issues?: Array<{ message?: string }> }).issues;
    if (issues?.[0]?.message) return issues[0].message;
  }
  return error instanceof Error ? error.message : 'Eingabe konnte nicht validiert werden.';
}

export function SurchargeLadderForm({
  clients,
  onCreated,
}: {
  clients: readonly { id: string; companyName: string | null }[];
  onCreated?: (ladderId: string) => void;
}) {
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState(initialDraft);
  const [formError, setFormError] = useState<string | null>(null);
  const create = trpc.surchargeLadders.create.useMutation({
    onSuccess: async (created) => {
      await utils.surchargeLadders.list.invalidate();
      setDraft(initialDraft());
      setFormError(null);
      toast.success('Neue Staffel-Version angelegt.');
      onCreated?.(created.id);
    },
    onError: (error) => toast.error(error.message),
  });

  const updateDraft = (patch: Partial<SurchargeLadderDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };
  const updateStep = (index: number, patch: Partial<LadderStepDraft>) => {
    setDraft((current) => ({
      ...current,
      steps: current.steps.map((step, stepIndex) =>
        stepIndex === index ? { ...step, ...patch } : step,
      ),
    }));
  };

  const submit = async () => {
    setFormError(null);
    try {
      await create.mutateAsync(buildSurchargeLadderInput(draft));
    } catch (error) {
      // TRPC-Fehler meldet der onError-Toast; lokale Validierungsfehler
      // erscheinen IMMER inline (kein stale `create.error`-Guard, s. PR #46).
      if (!(error instanceof TRPCClientError)) setFormError(validationMessage(error));
    }
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Field label="Staffel-Name" htmlFor="ladder-name">
          <Input
            id="ladder-name"
            value={draft.name}
            onChange={(event) => updateDraft({ name: event.target.value })}
            placeholder="z. B. Preis-Staffel Acme"
            required
          />
        </Field>
        <Field label="Berechnungsbasis" htmlFor="ladder-base">
          <Select
            value={draft.base}
            onValueChange={(value) => updateDraft({ base: value as SurchargeLadderDraft['base'] })}
          >
            <SelectTrigger id="ladder-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bill_rate">Kunden-Verrechnungssatz</SelectItem>
              <SelectItem value="wage">Worker-Lohn</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field
          label="Scope"
          htmlFor="ladder-client"
          hint="Global gilt für alle; eine Firma grenzt die Staffel auf diesen Kunden ein."
        >
          <Select
            value={draft.clientId ?? GLOBAL_SCOPE}
            onValueChange={(value) =>
              updateDraft({ clientId: value === GLOBAL_SCOPE ? undefined : value })
            }
          >
            <SelectTrigger id="ladder-client">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={GLOBAL_SCOPE}>Global</SelectItem>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.companyName ?? '(unbenannter Kunde)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Gültig ab" htmlFor="ladder-valid-from">
          <Input
            id="ladder-valid-from"
            type="date"
            value={draft.validFrom}
            onChange={(event) => updateDraft({ validFrom: event.target.value })}
            required
          />
        </Field>
        <div className="space-y-2 md:col-span-2">
          <span className="text-sm font-medium">Entgeltgruppen-Scope</span>
          <p className="text-xs text-[var(--muted-foreground)]">
            Leer = gilt für alle. TV-BZ-Blöcke (z. B. Chemie EG 1–2b) als getrennte Staffeln je
            Block anlegen.
          </p>
          <div className="flex flex-wrap gap-3">
            {ENTGELT_GROUPS.map((group) => (
              <label key={group} className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={draft.entgeltGroups.includes(group)}
                  onChange={() =>
                    updateDraft({
                      entgeltGroups: draft.entgeltGroups.includes(group)
                        ? draft.entgeltGroups.filter((value) => value !== group)
                        : [...draft.entgeltGroups, group],
                    })
                  }
                />
                {egLabel(group)}
              </label>
            ))}
          </div>
        </div>
        <Field label="Notiz" htmlFor="ladder-notes">
          <Textarea
            id="ladder-notes"
            value={draft.notes}
            onChange={(event) => updateDraft({ notes: event.target.value })}
            placeholder="Quelle, Vertragsbezug oder Änderungsgrund"
            className="min-h-9"
          />
        </Field>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-semibold">Stufen</h3>
            <p className="text-sm text-[var(--muted-foreground)]">
              Zeitlich aufsteigend; Unterbrechungen bis 3 Monate werden auf die Einsatzdauer
              angerechnet.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => updateDraft({ steps: [...draft.steps, emptyLadderStepDraft()] })}
          >
            + Stufe
          </Button>
        </div>

        {draft.steps.map((step, index) => (
          <Card key={index}>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle>Stufe {index + 1}</CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={draft.steps.length === 1}
                onClick={() =>
                  updateDraft({ steps: draft.steps.filter((_, stepIndex) => stepIndex !== index) })
                }
              >
                Entfernen
              </Button>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <Field label="Auslöser" htmlFor={`ladder-trigger-${index}`}>
                <Select
                  value={step.triggerType}
                  onValueChange={(value) =>
                    updateStep(index, { triggerType: value as LadderStepDraft['triggerType'] })
                  }
                >
                  <SelectTrigger id={`ladder-trigger-${index}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weeks">nach vollendeten Wochen</SelectItem>
                    <SelectItem value="months">nach vollendeten Monaten</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label={step.triggerType === 'weeks' ? 'Wochen (0 = ab Start)' : 'Monate'}
                htmlFor={`ladder-threshold-${index}`}
              >
                <Input
                  id={`ladder-threshold-${index}`}
                  type="number"
                  min="0"
                  step="1"
                  value={step.threshold}
                  onChange={(event) => updateStep(index, { threshold: event.target.value })}
                  required
                />
              </Field>
              <Field label="Prozent" htmlFor={`ladder-percent-${index}`}>
                <Input
                  id={`ladder-percent-${index}`}
                  type="number"
                  min="0"
                  max="1000"
                  step="0.01"
                  value={step.percent}
                  onChange={(event) => updateStep(index, { percent: event.target.value })}
                  required
                />
              </Field>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border)] pt-4">
        <p className="text-sm text-[var(--muted-foreground)]">
          Korrekturen erzeugen eine neue Version; bestehende Finanzhistorie wird nicht überschrieben.
        </p>
        <div className="flex items-center gap-3">
          {formError && <p className="max-w-lg text-sm text-[oklch(0.55_0.2_25)]">{formError}</p>}
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Speichere …' : 'Version anlegen'}
          </Button>
        </div>
      </div>
    </form>
  );
}
